import React from 'react'
import { Box, Card, Typography, Button, Divider, Stack, Tooltip } from '@mui/material'
import { styled } from '@mui/material/styles'
import { useAppTranslation } from 'src/admin/core/hooks'
import { CopyToClipboard } from 'react-copy-to-clipboard'
import { StatusLabel } from './StatusLabel'
import type { TicketSaleTicket, TicketSaleTransaction, TicketDetails } from 'src/admin/core/services/lottery/inquiryTicket'

export interface LotteryTicketReceiptProps {
  transaction: TicketSaleTransaction
  ticket: TicketSaleTicket
  ticketDetails: TicketDetails
  ticketStatus?: string
  totalPagar?: number
  mensaje?: string | null
  codigo?: string | null
}

/**
 * Maps API ticket status to uppercase for StatusLabel component
 * API values: Issued, Cancelled, Paid
 * StatusLabel values: ISSUED, CANCELLED, PAID
 */
const mapTicketStatus = (status?: string): string => {
  if (!status) return 'ISSUED'
  return status.toUpperCase()
}

const StyledCard = styled(Card)(({ theme }) => ({
  padding: theme.spacing(2),
  width: '100%',
  height: '100%',
  display: 'flex',
  flexDirection: 'column',
  borderRadius: theme.spacing(1),
  boxShadow: '0px 4px 10px rgba(0, 0, 0, 0.1)',
}))

const ReceiptField = styled(Box)(({ theme }) => ({
  display: 'flex',
  width: '100%',
  marginBottom: theme.spacing(0.5),
  fontFamily: "'Courier New', monospace",
}))

const StyledDivider = styled(Divider)(({ theme }) => ({
  width: '100%',
  margin: `${theme.spacing(1)} 0`,
  borderStyle: 'dashed',
  borderColor: theme.palette.divider,
}))

const hasValue = (v: any) =>
  v !== undefined && v !== null && v !== '' && !(typeof v === 'object' && Object.keys(v).length === 0)

const LotteryTicketReceipt: React.FC<LotteryTicketReceiptProps> = ({
  transaction,
  ticket,
  ticketDetails,
  ticketStatus,
  totalPagar,
  mensaje,
  codigo,
}) => {
  const { label } = useAppTranslation()
  const [copyVoidCode, setCopyVoidCode] = React.useState(false)

  return (
    <StyledCard>
      {/* Mensaje del response */}
      {mensaje && mensaje.trim() !== '' && (
        <Box
          sx={{
            p: 1.5,
            mb: 2,
            backgroundColor: 'warning.light',
            borderRadius: 1,
            textAlign: 'center',
          }}
        >
          <Typography variant="body2" color="info.contrastText" fontWeight="medium">
            {mensaje}
          </Typography>
        </Box>
      )}

      {codigo && codigo.trim() == '201' && (
        <Box display="flex" justifyContent="space-between" alignItems="center" mb={1}>
          <StatusLabel status={mapTicketStatus(ticketStatus) as any} />
          <Stack direction="row" spacing={1}>
            <Tooltip
              PopperProps={{
                disablePortal: true,
              }}
              onClose={() => setCopyVoidCode(false)}
              open={copyVoidCode}
              disableFocusListener
              disableHoverListener
              disableTouchListener
              title={label('COPY_TICKET_AUDIT') as string}
            >
              <Button color="secondary" variant="outlined" size="small">
                <CopyToClipboard
                  text={transaction?.no_trans || ''}
                  onCopy={() => {
                    setCopyVoidCode(true)
                    setTimeout(() => {
                      setCopyVoidCode(false)
                    }, 2000)
                  }}
                >
                  <span key="copy-number-text">{label('COPIAR_NUMERO_ANULAR') || 'Copiar para anular'}</span>
                </CopyToClipboard>
              </Button>
            </Tooltip>
          </Stack>
        </Box>
      )}

      <StyledDivider />

      {/* Transaction Information */}
      <Stack spacing={0.6} sx={{ fontFamily: "'Courier New', monospace" }}>
        {hasValue(ticketDetails?.number) && (
          <ReceiptField>
            <Typography sx={{ fontSize: '12px', fontWeight: 700 }}>TICKET:</Typography>
            <Typography sx={{ fontSize: '14px', ml: 1 }}>{ticketDetails.number}</Typography>
          </ReceiptField>
        )}
        {hasValue(transaction?.no_trans) && (
          <ReceiptField>
            <Typography sx={{ fontSize: '12px', fontWeight: 700 }}>NO. TRANSACCIÓN:</Typography>
            <Typography sx={{ fontSize: '14px', ml: 1 }}>{transaction.no_trans}</Typography>
          </ReceiptField>
        )}
        {/* {ticket?.cod_pago && (
          <ReceiptField>
            <Typography sx={{ fontSize: '12px', fontWeight: 700 }}>CÓDIGO DE PAGO:</Typography>
            <Typography sx={{ fontSize: '12px', ml: 1 }}>{ticket.cod_pago}</Typography>
          </ReceiptField>
        )} */}
        {hasValue(transaction?.vendedor) && (
          <ReceiptField>
            <Typography sx={{ fontSize: '12px', fontWeight: 700 }}>NO. VENDEDOR:</Typography>
            <Typography sx={{ fontSize: '12px', ml: 1 }}>{transaction.vendedor}</Typography>
          </ReceiptField>
        )}
        {hasValue(transaction?.fecha_hora_transaccion) && (
          <ReceiptField>
            <Typography sx={{ fontSize: '12px', fontWeight: 700 }}>FECHA DE EMISIÓN:</Typography>
            <Typography sx={{ fontSize: '12px', ml: 1 }}>{transaction.fecha_hora_transaccion}</Typography>
          </ReceiptField>
        )}

        <StyledDivider />

        {hasValue(transaction?.nombre_sorteo) && (
          <ReceiptField>
            <Typography sx={{ fontSize: '12px', fontWeight: 700 }}>SORTEO:</Typography>
            <Typography sx={{ fontSize: '12px', ml: 1 }}>{transaction.nombre_sorteo}</Typography>
          </ReceiptField>
        )}
        {hasValue(transaction?.fecha_hora_sorteo) && (
          <ReceiptField>
            <Typography sx={{ fontSize: '12px', fontWeight: 700 }}>FECHA DEL SORTEO:</Typography>
            <Typography sx={{ fontSize: '12px', ml: 1 }}>{transaction.fecha_hora_sorteo}</Typography>
          </ReceiptField>
        )}
        {hasValue(transaction?.no_sorteo) && (
          <ReceiptField>
            <Typography sx={{ fontSize: '12px', fontWeight: 700 }}>SORTEO:</Typography>
            <Typography sx={{ fontSize: '12px', ml: 1 }}>{transaction.no_sorteo}</Typography>
          </ReceiptField>
        )}
        {hasValue(ticket?.precio_fraccion) && (
          <ReceiptField>
            <Typography sx={{ fontSize: '12px', fontWeight: 700 }}>PRECIO:</Typography>
            <Typography sx={{ fontSize: '12px', ml: 1 }}>RD$ {ticket.precio_fraccion}</Typography>
          </ReceiptField>
        )}
        {hasValue(ticket?.no_ticket) && (
          <ReceiptField>
            <Typography sx={{ fontSize: '12px', fontWeight: 700 }}>NO. TICKET:</Typography>
            <Typography sx={{ fontSize: '12px', ml: 1 }}>{ticket.no_ticket}</Typography>
          </ReceiptField>
        )}
        {hasValue(ticket?.no_control) && (
          <ReceiptField>
            <Typography sx={{ fontSize: '12px', fontWeight: 700 }}>NO. CONTROL:</Typography>
            <Typography sx={{ fontSize: '12px', ml: 1 }}>{ticket.no_control}</Typography>
          </ReceiptField>
        )}
        {hasValue(ticket?.fracc_desde) && hasValue(ticket?.fracc_hasta) && (
          <ReceiptField>
            <Typography sx={{ fontSize: '12px', fontWeight: 700 }}>FRACCIÓN:</Typography>
            <Typography sx={{ fontSize: '12px', ml: 1 }}>
              {ticket.fracc_desde}-{ticket.fracc_hasta}
            </Typography>
          </ReceiptField>
        )}

        {/* Boleto Number - Large Display */}
        {hasValue(ticket?.no_boleto) && (
          <Box mt={1}>
            <Typography sx={{ fontSize: '12px', fontWeight: 700 }}>BOLETO NO.:</Typography>
            <Typography
              sx={{
                fontSize: '32px',
                fontWeight: 700,
                textAlign: 'center',
                fontFamily: "'Courier New', monospace",
                letterSpacing: '2px',
              }}
            >
              {ticket.no_boleto}
            </Typography>
            {hasValue(transaction?.cod_transaccion) && (
              <Typography
                sx={{
                  fontSize: '10px',
                  fontWeight: 400,
                  textAlign: 'center',
                  mt: 0,
                }}
              >
                {transaction.cod_transaccion}
              </Typography>
            )}
          </Box>
        )}

        <StyledDivider />

        {/* Total */}
        {/* {ticket?.total && (
          <Box display="flex" justifyContent="space-between" alignItems="center" mt={1}>
            <Typography sx={{ fontSize: '14px', fontWeight: 700 }}>TOTAL:</Typography>
            <Typography sx={{ fontSize: '16px', fontWeight: 700 }}>RD$ {ticket.total}</Typography>
          </Box>
        )} */}

        {/* Total a Pagar - Only show if greater than zero */}
        {typeof totalPagar === 'number' && totalPagar > 0 && (
          <>
            <StyledDivider />
            <Box display="flex" justifyContent="space-between" alignItems="center" mt={1}>
              <Typography sx={{ fontSize: '14px', fontWeight: 700, color: 'success.main' }}>
                {ticketStatus?.toUpperCase() === 'ISSUED' ? 'A PAGAR:' : 'TOTAL A PAGAR:'}
              </Typography>
              <Typography sx={{ fontSize: '18px', fontWeight: 700, color: 'success.main' }}>
                RD$ {totalPagar.toFixed(2)}
              </Typography>
            </Box>
          </>
        )}
      </Stack>
    </StyledCard>
  )
}

export default LotteryTicketReceipt
